import React, { ReactElement, useEffect, useLayoutEffect, useRef, useState } from 'react'
import sum from 'lodash/sum'
import { useDrag } from '@use-gesture/react'
import { useSprings, animated, to } from '@react-spring/web'
import Grid from '../Grid'
import Touchable from '../Touchable'
import { DraggableListProps } from './DraggableListProps'

type AnimationProps = {
    x: number
    y: number
    scale: number
    zIndex: number
    immediate: ((n: string) => boolean) | boolean
}

function swap<T>(array: T[], oldIndex: number, newIndex: number): T[] {
    const newOrder = array.slice(0)
    const element = array[oldIndex]
    newOrder.splice(oldIndex, 1)
    newOrder.splice(newIndex, 0, element)
    return newOrder
}

function getCurrentIndex(currentIndex: number, offset: number, heights: number[]): number {
    let sumVal = 0
    let currentRow = 0
    const startValue = sum(heights.slice(0, currentIndex))
    for (let i = 0; i <= heights.length; i++) {
        if (offset + startValue >= sumVal + heights[i] / 2) {
            currentRow = i + 1
            sumVal += heights[i]
        } else {
            break
        }
    }
    return currentRow
}

const getAnimationProps =
    (
        measuringUnits: number[],
        order: number[],
        dragScale: number,
        down?: boolean,
        originalIndex?: number,
        curIndex = 0,
        x = 0,
        y = 0
    ) =>
    (index: number): AnimationProps => {
        return down && index === originalIndex
            ? {
                  x: sum(measuringUnits.slice(0, curIndex)) + x,
                  y: sum(measuringUnits.slice(0, curIndex)) + y,
                  scale: dragScale,
                  zIndex: 1,
                  immediate: (n: string): boolean => n === 'x' || n === 'y' || n === 'zIndex',
              }
            : {
                  x: sum(measuringUnits.slice(0, order.indexOf(index))),
                  y: sum(measuringUnits.slice(0, order.indexOf(index))),
                  scale: 1,
                  zIndex: 0,
                  immediate: false,
              }
    }

const DraggableList = <T extends {}>({
    horizontal = false,
    dragScale = 1.2,
    spaceBetween = 0,
    items,
    renderItem,
    keyExtractor,
    onDragEnd,
    onDragBegin,
    onItemPress,
}: DraggableListProps<T>): ReactElement => {
    const refs = useRef<(HTMLDivElement | null)[]>([])
    const [measuringUnits, setMeasuringUnits] = useState<number[]>([])
    const [isDragging, setIsDragging] = useState(false)

    const order = useRef(items.map((_, index) => index))
    useEffect(() => {
        order.current = items.map((_, index) => index)
    }, [items, order])

    const [springs, api] = useSprings<AnimationProps>(
        items.length,
        getAnimationProps(measuringUnits, order.current, dragScale),
        [items]
    )

    useLayoutEffect(() => {
        items.forEach((_, index) => {
            if (horizontal) {
                const clientWidth = refs.current[index]?.clientWidth
                measuringUnits[index] = clientWidth ? clientWidth + spaceBetween : 0
            } else {
                const clientHeight = refs.current[index]?.clientHeight
                measuringUnits[index] = clientHeight ? clientHeight + spaceBetween : 0
            }
            setMeasuringUnits(measuringUnits)
            api.start(getAnimationProps(measuringUnits, order.current, dragScale))
        })
    }, [measuringUnits, horizontal, items, refs, api, dragScale, spaceBetween])

    const bind = useDrag(({ args: [originalIndex], down, movement: [x, y] }) => {
        if (!isDragging) {
            setIsDragging(true)
            if (onDragBegin) {
                onDragBegin()
            }
        }

        const startIndex = order.current.indexOf(originalIndex)
        const endIndex = getCurrentIndex(startIndex, horizontal ? x : y, measuringUnits)
        const newOrder = swap(order.current, startIndex, endIndex)
        api.start(
            getAnimationProps(
                measuringUnits,
                newOrder,
                dragScale,
                down,
                originalIndex,
                startIndex,
                x,
                y
            )
        )

        if (!down && ((horizontal && x !== 0) || (!horizontal && y !== 0))) {
            if (onDragEnd) {
                setIsDragging(false)
                onDragEnd(newOrder)
            }
            order.current = newOrder
        }
    })

    return (
        <Grid centerVertical={horizontal} centerHorizontal={!horizontal} userSelect="none">
            {springs.map(({ zIndex, x, y, scale }, i: number) => {
                const item = items[i]
                return (
                    <Touchable
                        key={keyExtractor(item, i)}
                        onPress={onItemPress ? (): void => onItemPress(item) : undefined}
                    >
                        <animated.div
                            ref={(ref): void => {
                                refs.current[i] = ref
                            }}
                            {...bind(i)}
                            style={{
                                userSelect: 'none',
                                zIndex,
                                transform: horizontal
                                    ? to(
                                          [x, scale],
                                          (x, s) => `translate3d(${x}px,0,0) scale(${s})`
                                      )
                                    : to(
                                          [y, scale],
                                          (y, s) => `translate3d(0,${y}px,0) scale(${s})`
                                      ),
                                position: 'absolute',
                            }}
                        >
                            {renderItem(item)}
                        </animated.div>
                    </Touchable>
                )
            })}
        </Grid>
    )
}

export default DraggableList
